import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { TextInput } from '@jotta/ui/TextInput'
import { CTAButton } from './CTAButton'
import type { SignupRequestCallback } from './CreateUser'
import { useForm } from '@jotta/hooks'
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox'
import {
  useAcceptedTermsField,
  useEmailField,
  newsletterField,
  usePasswordField,
  useNameField,
  useActivationCodeTerminology,
  useVoucherCodeField,
  customerGroupCodeField,
} from './signupForm'
import { FormField } from '@jotta/ui/FormField'
import { Form } from '@jotta/ui/Form'
import { PasswordField } from './PasswordField'

export function CreateVoucherUser({
  customerGroupCode,
  className,
  onRequestLogin,
  isPending,
  signup,
  prefill = {},
}: {
  customerGroupCode: string
  className: string
  onRequestLogin?: () => void
  isPending: boolean
  signup: SignupRequestCallback
  prefill?: { name?: string; email?: string; voucherCode?: string }
}) {
  const activationCodeTerminology = useActivationCodeTerminology()

  const form = useForm({
    fields: {
      customerGroupCode: customerGroupCodeField,
      voucherCode: useVoucherCodeField(
        customerGroupCode,
        voucherCustomerGroupCode =>
          form.set('customerGroupCode', voucherCustomerGroupCode),
      ),
      name: useNameField(),
      email: useEmailField(customerGroupCode, onRequestLogin),
      password: usePasswordField(),
      acceptedTerms: useAcceptedTermsField(),
      newsletter: newsletterField,
    },
    defaultValues: () => prefill,
    submit: ({
      voucherCode,
      customerGroupCode,
      name,
      email,
      password,
      newsletter,
    }) =>
      signup({
        request: {
          case: 'formSignup',
          value: {
            fullname: name,
            email: email,
            password: password,
          },
        },
        voucherCode,
        customerGroupCode,
        wantsNewsletter: Boolean(newsletter),
      }),
  })

  const {
    voucherCode,
    name,
    password,
    email,
    acceptedTerms,
    newsletter,
    isSubmitting,
  } = form

  return (
    <Form form={form} className={className}>
      <FormField
        label={
          activationCodeTerminology
            ? t`Type your activation code`
            : t`Type your voucher code`
        }
        field={voucherCode}
      >
        <TextInput {...voucherCode.inputProps} />
      </FormField>

      {voucherCode.value && (
        <>
          <FormField label={t`Name`} field={name}>
            <TextInput {...name.inputProps} />
          </FormField>

          <FormField label={t`Email`} field={email}>
            <TextInput {...email.inputProps} />
          </FormField>

          <PasswordField field={password} />

          <TermsAndConditionsCheckbox
            acceptedTerms={acceptedTerms}
            newsletter={newsletter}
          />

          <CTAButton loading={isPending || isSubmitting} type="submit">
            <Trans>Create account</Trans>
          </CTAButton>
        </>
      )}
    </Form>
  )
}
